import React, { useState } from 'react'

import DOMPurify from 'dompurify'

import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import CheckBox from '../../controls/CheckBox'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'
import Select from '../../controls/Select'
import TextArea from '../../controls/TextArea'

export default {
  name: 'Work Status',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  return validation.object({
    status: validation
      .object({
        history: validation
          .arrayOf(
            validation.object({
              dateLastWorked: validation.date('Date Last Worked').nullable(),
              date: validation.date('Date Reported').nullable(),
              treatmentProvider: validation.shortText('Treatment Provider').nullable(),
              currentTreatment: validation.richText('Current Treatment').nullable(),
              workStatus: validation.shortText('Work Status').nullable(),
              returnStatus: validation.richText('Return to Work Status').nullable(),
              lightDuty: validation.boolean().nullable(),
            }),
          )
          .nullable(),
      })
      .nullable(),
  })
}

function Component({
  addItemToArray,
  removeItemFromArray,
  createInputProps,
  getValue,
  setModelValue,
  setModelValues,
  ErrorMessage,
  caseModel,
}) {
  const [editingIndex, setEditingIndex] = useState(null)
  const history = getValue('status.history', [])

  const handleAddEntry = () => {
    const nextIndex = history.length
    addItemToArray('status.history', {
      dateLastWorked: null,
      date: null,
      treatmentProvider: '',
      currentTreatment: '',
      workStatus: '',
      returnStatus: '',
      lightDuty: false,
    })
    setEditingIndex(nextIndex)
  }

  const handleRemove = index => {
    const newHistory = [...history.slice(0, index), ...history.slice(index + 1)]
    if (newHistory.length === 0) {
      if (setModelValues) {
        setModelValues([
          ['status.history', []],
          ['status.workStatus', null],
          ['status.returnStatus', null],
          ['status.date', null],
          ['status.treatmentProvider', null],
          ['status.currentTreatment', null],
          ['status.dateLastWorked', null],
          ['status.lightDuty', false],
          ['workStatus.status', null],
          ['workStatus.workCapacity', null],
          ['workStatus.restrictions', null],
          ['workStatus.effectiveDate', null],
        ])
      } else {
        removeItemFromArray('status.history', index)
        if (setModelValue) {
          setModelValue('status.workStatus', null)
          setModelValue('status.returnStatus', null)
          setModelValue('status.date', null)
          setModelValue('status.treatmentProvider', null)
          setModelValue('status.currentTreatment', null)
          setModelValue('status.dateLastWorked', null)
          setModelValue('status.lightDuty', false)
          setModelValue('workStatus.status', null)
          setModelValue('workStatus.workCapacity', null)
          setModelValue('workStatus.restrictions', null)
          setModelValue('workStatus.effectiveDate', null)
        }
      }
    } else {
      removeItemFromArray('status.history', index)
    }
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <div>
      <h4 className="pb-3">Work Status</h4>
      {history.map((x, index) => {
        const namePrefix = `status.history[${index}]`
        const isEditing = editingIndex === index
        return (
          <React.Fragment key={namePrefix}>
            <WorkStatusEntry
              namePrefix={namePrefix}
              index={index}
              createInputProps={createInputProps}
              ErrorMessage={ErrorMessage}
              handleRemove={() => handleRemove(index)}
              caseModel={caseModel}
              getValue={getValue}
              isEditing={isEditing}
              onEdit={() => setEditingIndex(index)}
              onDone={() => setEditingIndex(null)}
            />
            <hr />
          </React.Fragment>
        )
      })}
      <AddButton id="addWorkStatusButton" onClick={handleAddEntry}>
        Add Work Status Entry
      </AddButton>
    </div>
  )
}

function WorkStatusEntry({
  namePrefix,
  index,
  createInputProps,
  ErrorMessage,
  handleRemove,
  caseModel,
  getValue,
  isEditing,
  onEdit,
  onDone,
}) {
  const dateLastWorked = getValue(`${namePrefix}.dateLastWorked`, '')
  const date = getValue(`${namePrefix}.date`, '')
  const treatmentProvider = getValue(`${namePrefix}.treatmentProvider`, '')
  const currentTreatment = getValue(`${namePrefix}.currentTreatment`, '')
  const workStatus = getValue(`${namePrefix}.workStatus`, '')
  const returnStatus = getValue(`${namePrefix}.returnStatus`, '')
  const lightDuty = getValue(`${namePrefix}.lightDuty`)

  const treatmentProviders = caseModel?.treatmentProviders || []
  const providerOptions = treatmentProviders.map(p => ({
    value: p.name,
    label: p.name,
  }))
  if (treatmentProvider && !providerOptions.some(opt => opt.value === treatmentProvider)) {
    providerOptions.push({
      value: treatmentProvider,
      label: treatmentProvider,
    })
  }
  const hasReturnStatus = Boolean(returnStatus && returnStatus.replace(/<[^>]*>/g, '').trim())
  const hasCurrentTreatment = Boolean(currentTreatment && currentTreatment.replace(/<[^>]*>/g, '').trim())

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">
              {workStatus ||
                (hasReturnStatus ? (
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(returnStatus) }} />
                ) : (
                  <em className="text-muted">Untitled Work Status</em>
                ))}
            </span>
            {date && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                Reported: {date}
              </span>
            )}
            {dateLastWorked && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                Last Worked: {dateLastWorked}
              </span>
            )}
            {lightDuty && <span className="badge badge-warning ml-3">Light Duty</span>}
            {treatmentProvider && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                Provider: {treatmentProvider}
              </span>
            )}
          </div>
          {((hasReturnStatus && workStatus) || hasCurrentTreatment) && (
            <div className="text-muted mt-1" style={{ fontSize: '0.85em' }}>
              {hasReturnStatus && workStatus && (
                <div className="mb-1">
                  <span>Return Status: </span>
                  <div className="d-inline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(returnStatus) }} />
                </div>
              )}
              {hasCurrentTreatment && (
                <div>
                  <span>Treatment: </span>
                  <div
                    className="d-inline"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentTreatment) }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={onEdit}
            title="Edit this work status entry"
          >
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="clearfix">
      <div className="form-row">
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.dateLastWorked`}>
            Date Last Worked
          </label>
          <DatePicker id={`${namePrefix}.dateLastWorked`} {...createInputProps(`${namePrefix}.dateLastWorked`)} />
          <ErrorMessage name={`${namePrefix}.dateLastWorked`} />
        </div>
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.date`}>
            Date Reported
          </label>
          <DatePicker id={`${namePrefix}.date`} {...createInputProps(`${namePrefix}.date`)} />
          <ErrorMessage name={`${namePrefix}.date`} />
        </div>
        <div className="form-group col-md-4">
          <label className="form-label" htmlFor={`${namePrefix}.treatmentProvider`}>
            Treatment Provider
          </label>
          <Select
            {...createInputProps(`${namePrefix}.treatmentProvider`)}
            value={providerOptions.find(x => x.value === getValue(`${namePrefix}.treatmentProvider`)) || null}
            options={providerOptions}
            placeholder="Select provider"
            isClearable
          />
          <ErrorMessage name={`${namePrefix}.treatmentProvider`} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group col-md-6">
          <label className="form-label" htmlFor={`${namePrefix}.workStatus`}>
            Work Status
          </label>
          <input
            type="text"
            className="form-control"
            id={`${namePrefix}.workStatus`}
            maxLength={validation.maxLengths.shortText}
            {...createInputProps(`${namePrefix}.workStatus`)}
          />
          <ErrorMessage name={`${namePrefix}.workStatus`} />
        </div>
        <div className="form-group col-md-6 d-flex align-items-end pb-2">
          <CheckBox id={`${namePrefix}.lightDuty`} {...createInputProps(`${namePrefix}.lightDuty`)}>
            Light Duty Options
          </CheckBox>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group col-md-6">
          <label className="form-label" htmlFor={`${namePrefix}.returnStatus`}>
            Return to Work Status / Restrictions
          </label>
          <TextArea
            areaProps={{ ...createInputProps(`${namePrefix}.returnStatus`) }}
            rows={3}
            maxLength={validation.maxLengths.richText}
          />
          <ErrorMessage name={`${namePrefix}.returnStatus`} />
        </div>
        <div className="form-group col-md-6">
          <label className="form-label" htmlFor={`${namePrefix}.currentTreatment`}>
            Current Treatment
          </label>
          <TextArea
            areaProps={{ ...createInputProps(`${namePrefix}.currentTreatment`) }}
            rows={3}
            maxLength={validation.maxLengths.richText}
          />
          <ErrorMessage name={`${namePrefix}.currentTreatment`} />
        </div>
      </div>
      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
